import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {CloseIcon, MenuIcon, ShirtIcon} from '../icons';
import {ThemeToggle} from '../ThemeToggle';
import {appName} from './content';
import {LandingCta} from './cta';

/** Якорная навигация лендинга; `scroll-mt-*` на секциях компенсирует sticky-шапку. */
const NAV_LINKS = [
    {href: '#features', label: 'Возможности'},
    {href: '#how', label: 'Как работает'},
    {href: '#intelligence', label: 'AI и аналитика'},
    {href: '#security', label: 'Безопасность'},
];

/**
 * Шапка публичной страницы: логотип, якоря, переключатель темы и CTA.
 * Primary в этом decision region ровно один: анониму — «Создать мой шкаф»,
 * вошедшему пользователю — «Перейти в гардероб» (вторичного входа у него нет).
 */
const LandingHeader: React.FC<{cta: LandingCta}> = ({cta}) => {
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <header className="sticky top-0 z-40 backdrop-blur bg-white/85 dark:bg-ink-950/85 border-b border-ink-100 dark:border-white/5">
            <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6 h-16 flex items-center gap-4">
                <Link to="/" className="flex items-center gap-2 shrink-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500">
                    <span className="w-9 h-9 rounded-xl bg-brand-500 flex items-center justify-center">
                        <ShirtIcon className="w-5 h-5 text-ink-900"/>
                    </span>
                    <span className="text-lg font-bold text-ink-900 dark:text-ink-50">{appName()}</span>
                </Link>

                <nav aria-label="Разделы страницы" className="hidden lg:flex items-center gap-6 mx-auto">
                    {NAV_LINKS.map(link => (
                        <a
                            key={link.href}
                            href={link.href}
                            className="text-sm font-medium text-ink-600 dark:text-ink-300 hover:text-ink-900 dark:hover:text-white transition-colors rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="ml-auto lg:ml-0 flex items-center gap-2 sm:gap-3">
                    <ThemeToggle/>
                    {cta.login && (
                        <Link to={cta.login.href} className="hidden sm:inline-flex btn-secondary h-10">
                            {cta.login.label}
                        </Link>
                    )}
                    <Link to={cta.primary.href} className="hidden sm:inline-flex btn-accent h-10">
                        {cta.primary.label}
                    </Link>
                    <button
                        type="button"
                        className="lg:hidden p-2.5 rounded-xl text-ink-600 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500"
                        aria-expanded={menuOpen}
                        aria-controls="landing-mobile-menu"
                        aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'}
                        onClick={() => setMenuOpen(open => !open)}
                    >
                        {menuOpen ? <CloseIcon className="w-6 h-6"/> : <MenuIcon className="w-6 h-6"/>}
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div
                    id="landing-mobile-menu"
                    className="lg:hidden border-t border-ink-100 dark:border-white/5 bg-white dark:bg-ink-950 px-4 py-4 space-y-1"
                >
                    {NAV_LINKS.map(link => (
                        <a
                            key={link.href}
                            href={link.href}
                            className="block px-3 py-3 rounded-xl text-sm font-medium text-ink-700 dark:text-ink-200 hover:bg-ink-50 dark:hover:bg-white/5"
                            onClick={() => setMenuOpen(false)}
                        >
                            {link.label}
                        </a>
                    ))}
                    <div className="pt-2 flex flex-col gap-2 sm:hidden">
                        {cta.login && (
                            <Link to={cta.login.href} className="btn-secondary h-11" onClick={() => setMenuOpen(false)}>
                                {cta.login.label}
                            </Link>
                        )}
                        <Link to={cta.primary.href} className="btn-accent h-11" onClick={() => setMenuOpen(false)}>
                            {cta.primary.label}
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default LandingHeader;
